import { HoursPanel } from '@/components/partial/ResourceSettingsPanel/HoursPanel';
import GeneralTab from '@/components/resources/tabs/GeneralTab';
import { GeneralTabMultiple } from '@/components/resources/tabs/GeneralTabMultiple';
import RestrictionsTab from '@/components/resources/tabs/access-restrictions/RestrictionsTab';
import { classNames } from '@/lib/classNames';
import { IResource } from '@gettactic/api';
import { useAuthenticated } from '@gettactic/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@v2/components/ui/tabs';

type Props = {
  resources: IResource[];
  notifyUpdateLabels?: (resWithLabels: IResource[]) => void;
  onSaved?: () => void;
};

export default function ResourcePanel({
  resources,
  notifyUpdateLabels,
  onSaved
}: Props) {
  const {
    userContext: { currentOffice }
  } = useAuthenticated();
  const queryClient = useQueryClient();
  const isSingle = resources.length === 1;
  const afterEdit = async () => {
    queryClient.invalidateQueries([
      'organizations.resourcesAll',
      currentOffice.id
    ]);
  };

  const tabs: {
    id: string;
    title: string;
    condition: boolean;
    render: () => JSX.Element | null;
  }[] = [
    {
      id: 'general',
      title: 'General',
      condition: true,
      render: () =>
        isSingle ? (
          <GeneralTab
            mutate={afterEdit}
            resource={resources[0]}
            onSaved={onSaved}
          />
        ) : (
          <GeneralTabMultiple
            notifyUpdateLabels={notifyUpdateLabels}
            mutate={afterEdit}
            resources={resources}
            key={resources.map((x) => x.id).join(',')}
            onSaved={onSaved}
          />
        )
    },
    {
      id: 'hours',
      title: 'Hours',
      condition: true,
      render: () => <HoursPanel resources={resources} office={currentOffice} />
    },
    {
      id: 'restrictions',
      title: 'Assignments',
      condition: true,
      render: () => (
        <RestrictionsTab
          mutateAll={afterEdit}
          resources={resources}
          key={resources.map((x) => x.id).join(',')}
        />
      )
    }
  ];

  const visibleTabs = tabs.filter((tabDef) => tabDef.condition);
  const [selectedTab, setSelectedTab] = useState(visibleTabs[0]?.id || '');
  return (
    <Tabs
      value={selectedTab}
      onValueChange={setSelectedTab}
      defaultValue={visibleTabs[0]?.id}
      className="contents"
    >
      <TabsList
        aria-label="Permission tabs"
        className="col-span-12 flex flex-none gap-8 border-b border-gray-200 text-base"
      >
        {visibleTabs.map((tabDef) => (
          <TabsTrigger
            key={tabDef.id}
            value={tabDef.id}
            className={classNames(
              'whitespace-nowrap border-b-2 px-1 py-4 font-medium focus:border-primary focus:outline-none focus:ring-primary',
              'border-transparent text-gray-800 hover:border-gray-300 hover:text-gray-600',
              'data-[state=active]:border-primary data-[state=active]:text-primary'
            )}
            type="button"
          >
            {tabDef.title}
          </TabsTrigger>
        ))}
      </TabsList>
      {visibleTabs.map((tabDef) => (
        <TabsContent
          key={tabDef.id}
          value={tabDef.id}
          className="col-span-12 flex h-full flex-auto flex-col flex-nowrap overflow-hidden focus:border-primary focus:outline-none focus:ring-primary"
        >
          {selectedTab === tabDef.id ? tabDef.render() : null}
        </TabsContent>
      ))}
    </Tabs>
  );
}
